/**
 * Design tokens for the MUI-free `src/ui` layer.
 *
 * Values marked `lis-ds` below are copied verbatim from
 * `lis-ds/styles/lis-tokens.css` (generated by `lis-ds/tokens.mjs` from
 * `lis-ds/src/theme.js`) so this file stays a byte-for-byte mirror of the
 * canonical design system. Values marked mockup-only exist only in the
 * `lis-taotlus-wizard-mockup` artifact and have no home in `lis-ds` yet.
 *
 * Enforceable rule (the previous "no literal value anywhere" version of this
 * paragraph was violated 75 times over):
 *  - colours and font-family: never literal.
 *  - font-size / font-weight / line-height: always through `typeStep()`
 *    (`src/ui/type.ts`), which reads `theme.font.scale` — the CVI type
 *    scale, the only source of type sizing here. Nothing below 14px.
 *  - spacing: any padding/margin/gap of 2, 4, 6, 8, 12, 16, 24, 32, 40, 48,
 *    56 or 64px reads from `theme.space`. 1px hairlines/borders, values
 *    inside `calc()` paired with an intrinsic size, and tuned off-grid
 *    values (3, 5, 9, 10, 11, 13, 14, 18, 20, 22, 26, 28, 30, 44, 72px) stay
 *    literal WITH a one-line comment naming what they are tuned against.
 *    Adding a new literal from the tokenised set above is a review failure.
 */
const tokens = {
  colors: {
    // lis-ds: --lis-color-primary
    primary: '#FFDD4F',
    // lis-ds: --lis-color-tan (also --lis-color-primary-contrasttext)
    brown: '#534A43',
    // lis-ds: --lis-color-secondary
    red: '#FF4642',
    // lis-ds: --lis-color-neutral
    neutral: '#A8A7A6',
    // lis-ds: --lis-color-pale
    pale: '#FFFFFF',
    // lis-ds: --lis-color-background
    background: '#FFFFFF',
    // lis-ds: --lis-color-surface
    surface: '#FFFFFF',
    // lis-ds: --lis-color-border-subtle
    borderSubtle: '#EAEEEF',
    // lis-ds: --lis-color-input-disabled-bg
    inputDisabledBg: '#EAEEEF',
    // lis-ds: --lis-color-text-primary
    textPrimary: 'rgba(0, 0, 0, 0.87)',
    // lis-ds: --lis-color-text-secondary
    textSecondary: 'rgba(0, 0, 0, 0.6)',
    // lis-ds: --lis-color-text-disabled
    textDisabled: 'rgba(0, 0, 0, 0.38)',
    // lis-ds: --lis-color-divider
    divider: 'rgba(0, 0, 0, 0.12)',
    // CVI status colours (Figma "TLL web UI NEW"), adopted from the TLL CVI —
    // Status/Red, Accent/Green (used as the success indicator because
    // Status/Green #37B97D is only 2.50:1 on white and is therefore
    // `successLight`, a decorative fill only — never used alone to convey
    // state), Status/Orange, Status/Blue. Usage contract (measured with
    // `contrastRatio`, all on white unless stated):
    //  - `main` (the unsuffixed key) = icons, dots, borders on white. All
    //    clear 3:1 (error 4.02, success 3.83, warning 3.10, info 3.47) but
    //    NEVER text: no foreground reaches 4.5:1 on any main (best case is
    //    `brownDark` on warning, 4.53).
    //  - `*Tint` = status surfaces, with `brown` text (7.02–7.52:1) or
    //    `brownDark` (11.40–12.21:1). Tinted surfaces carry NO hue border —
    //    the hue measures only 2.58–3.33:1 on its own tint, never a
    //    meaningful boundary.
    //  - `*Dark` = the ONLY fill allowed under white/`pale` text
    //    (4.53–4.57:1).
    // lis-ds: --lis-color-error
    error: '#DD4C4E',
    // lis-ds: --lis-color-error-dark
    errorDark: '#CE4749',
    // lis-ds: --lis-color-error-tint
    errorTint: '#FAE2E3',
    // lis-ds: --lis-color-success
    success: '#40916C',
    // lis-ds: --lis-color-success-light — Status/Green, 2.50:1 on white;
    // decorative fills only, see the usage contract above.
    successLight: '#37B97D',
    // lis-ds: --lis-color-success-dark
    successDark: '#28855A',
    // lis-ds: --lis-color-success-tint
    successTint: '#DFF4EA',
    // lis-ds: --lis-color-warning
    warning: '#F86300',
    // lis-ds: --lis-color-warning-dark
    warningDark: '#C95000',
    // lis-ds: --lis-color-warning-tint
    warningTint: '#FEE6D6',
    // lis-ds: --lis-color-info
    info: '#398CE4',
    // lis-ds: --lis-color-info-dark
    infoDark: '#3178C4',
    // lis-ds: --lis-color-info-tint
    infoTint: '#DFEDFB',
    // lis-ds: --lis-color-chip-default-bg / --lis-color-chip-default-text.
    // The slate "default contained" chip LIS adds on top of MUI's own set.
    chipDefaultBg: '#4F5A65',
    chipDefaultText: '#FFFFFF',

    // Mockup-only colours below — lis-ds now carries these under a
    // `--lis-color-wizard-*` prefix (see lis-tokens.css), so the upstream
    // TODOs are resolved; only the CSS custom property name differs from
    // the token key here (see `CSS_COLOR_VAR_NAME` in `lis-scope.tsx`).
    // lis-ds: --lis-color-wizard-yellow-dark
    yellowDark: '#E9C531',
    // lis-ds: --lis-color-wizard-yellow-pale
    yellowPale: '#FFF7D6',
    // lis-ds: --lis-color-wizard-brown-soft
    brownSoft: '#6B615A',
    // lis-ds: --lis-color-wizard-brown-dark
    // The CVI's primary text colour, 14.04:1 on white. For the few places
    // brown is not dark enough to clear 4.5:1 - notably a label on the
    // Smart-ID brand teal, where brown manages only 3.18:1.
    brownDark: '#332A23',
    // lis-ds: --lis-color-wizard-muted
    // mockup used #8A8178 (3.8:1) — darkened to pass WCAG AA (5.1:1 on white, 5.0:1 on surfaceMuted);
    // lis-ds palette.wizard.muted must follow.
    muted: '#766C63',
    // lis-ds: --lis-color-wizard-field-border
    // The only boundary of every input, checkbox, selectable card, secondary
    // button and dropzone, so SC 1.4.11 applies: 3:1 against the surface. The
    // mockup's 0.28 measured 1.99:1; this measures 3.31-3.36:1.
    fieldBorder: 'rgba(0, 0, 0, 0.45)',
    // lis-ds: --lis-color-wizard-disc
    disc: '#E8E8E6',
    // lis-ds: --lis-color-wizard-smart-id
    smartId: '#00AFAA',
    // lis-ds: --lis-color-wizard-mobile-id
    mobileId: '#076CE3',
    // lis-ds: --lis-color-wizard-surface-muted
    surfaceMuted: '#FCFCFB',
    // lis-ds: --lis-color-wizard-note-bg
    noteBg: '#FCFBF7',

    // mockup `.dialog-backdrop` overlay.
    // lis-ds: palette.wizard.backdrop (--lis-color-wizard-backdrop)
    backdrop: 'rgba(40, 36, 32, 0.45)',
  },

  font: {
    // lis-ds: --lis-font-family
    family: 'Lato, "Helvetica Neue", Arial, sans-serif',
    // lis-ds: --lis-font-weight-*. Named to match `scale` below. The Lato
    // family this app loads ships only weights 100/300/400/700/900, so a
    // requested 500 (`medium`) renders as 400 and 600 renders as 700 —
    // `medium` exists to name the CVI spec, not to promise a distinct face.
    weight: {
      regular: '400',
      medium: '500',
      bold: '700',
    },
    // The TLL CVI type scale (Figma "TLL web UI NEW"). FLOOR IS 14px —
    // nothing in this layer may go smaller. WCAG large text starts at 24px
    // (18.66px when bold), so every step below `display2xl` is judged at the
    // 4.5:1 normal-text bar, not the 3:1 large-text one. `display*` is what
    // the CVI sets in Core Sans M 55; LIS renders it in Lato instead (decision
    // recorded in the CVI review) — see the Lato weight note above for why
    // `medium`/600 do not produce a visually distinct weight from 400/700.
    // Read these via `typeStep()` (`src/ui/type.ts`), not by indexing here.
    scale: {
      // Body/xs — labels, chips, overlines, column headers, stat captions.
      xs: { size: '14px', lineHeight: '15.4px', weight: '500', letterSpacing: '1px', textTransform: 'uppercase' },
      // Body/sm — secondary body, helper/error text, table cells.
      sm: { size: '14px', lineHeight: '18.2px', weight: '400' },
      // Body/base — primary reading size.
      base: { size: '16px', lineHeight: '22.4px', weight: '400' },
      // Body/lg
      lg: { size: '18px', lineHeight: '25.2px', weight: '400' },
      // Body/xl
      xl: { size: '23px', lineHeight: '34.5px', weight: '400' },
      displayLg: { size: '18px', lineHeight: '21.6px', weight: '700' },
      displayXl: { size: '23px', lineHeight: '27.6px', weight: '700' },
      display2xl: { size: '28px', lineHeight: '33.6px', weight: '700' },
      display3xl: { size: '33px', lineHeight: '39.6px', weight: '700' },
      display4xl: { size: '40px', lineHeight: '44px', weight: '700' },
    },
  },

  // lis-ds: --lis-space-1 .. --lis-space-8 (8px steps)
  space: {
    1: '8px',
    2: '16px',
    3: '24px',
    4: '32px',
    5: '40px',
    6: '48px',
    7: '56px',
    8: '64px',
    // Sub-grid steps for inside-a-component geometry only (icon-to-label
    // gaps, chip padding, etc.) — never for spacing between components,
    // which stays on the 8px steps above. TODO(lis-ds): upstream.
    quarter: '2px',
    half: '4px',
    threeQuarter: '6px',
    onePointFive: '12px',
  },

  // lis-ds: --lis-radius
  radius: '4px',
  // lis-ds: --lis-radius-chip — every LIS chip is 5px, not a pill.
  radiusChip: '5px',
  // Larger radius used for cards/dialogs.
  // lis-ds: shape.borderRadiusLg (--lis-radius-lg)
  radiusLg: '6px',

  shadow: {
    // lis-ds: --lis-shadow-1
    1: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    // lis-ds: --lis-shadow-8
    8: '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    // mockup `.field input:focus` — colors.brown at 12% alpha.
    // lis-ds: lisShadows.focus (--lis-shadow-focus)
    focus: '0 0 0 3px rgba(83, 74, 67, 0.12)',
  },
} as const;

export default tokens;
